import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  GET_BUY_AGAIN,
  GET_PREFILL_CART,
  GET_SUGGESTED_ITEMS,
  toSuggestedQuantities,
  type IBuyAgain,
  type IPrefillCart,
  type IPrefillCartVariables,
  type IRecommendationVariables,
  type ISuggestedItems,
  type RecommendedProduct,
} from '@commerce/core'

/** The design draws three lines. More would push the grid off the first screen. */
const BASKET_SIZE = 3

/** Which field answered, so the section can say where the basket came from. */
export type BasketSource = 'prefillCart' | 'buyAgain' | 'suggestedItems'

export interface UseSuggestedBasket {
  products: RecommendedProduct[]
  /** `sku -> units`, as the server proposed them. */
  quantities: Record<string, number>
  source: BasketSource | null
  loading: boolean
}

const itemsOf = (products: RecommendedProduct[] | null | undefined): RecommendedProduct[] =>
  (products ?? []).filter((product) => product?.sku && product.isActive !== false)

/**
 * The basket behind "Sugerido para ti", from whichever field the storefront
 * actually has.
 *
 * What it replaces: the first three sellable products of the catalog, with the
 * quantities `[4, 3, 2]` hard-coded from the design mock. It proposed the same
 * basket to every shopper, in catalog order, and the numbers were decoration.
 *
 * Three sources, tried in order, because no storefront has all three and most
 * have exactly one:
 *
 * 1. `prefillCart` — the storefront's own proposed basket. It exists to answer
 *    this question, so it wins when it answers.
 * 2. `buyAgain` — what this customer reorders. The server dedups it and
 *    excludes what is already in the cart.
 * 3. `suggestedItems` — the ML suggestions, which is what a storefront with no
 *    purchase history for this customer can still offer.
 *
 * The cascade is `skip`, not a chain of callbacks: each query waits for the one
 * above it to come back empty, so a storefront with `prefillCart` configured
 * pays exactly one round trip and never learns the other two exist.
 *
 * **Nothing is invented.** All three empty means no basket and no section —
 * the same choice the reels make when there are no promotions. A made-up
 * basket is worse than none: it teaches the shopper that the suggestion is
 * noise.
 */
export const useSuggestedBasket = (): UseSuggestedBasket => {
  const { slug = '', sessionId = '' } = useParams()
  const variables = { storefrontName: slug, sessionUid: sessionId, limit: BASKET_SIZE }

  const prefill = useQuery<IPrefillCart, IPrefillCartVariables>(GET_PREFILL_CART, { variables })
  const prefillItems = itemsOf(prefill.data?.prefillCart)
  const prefillAnswered = !prefill.loading && (prefill.data !== undefined || prefill.error)

  const buyAgain = useQuery<IBuyAgain, IRecommendationVariables>(GET_BUY_AGAIN, {
    variables,
    skip: !prefillAnswered || prefillItems.length > 0,
  })
  const buyAgainItems = itemsOf(buyAgain.data?.buyAgain?.items)
  const buyAgainAnswered = !buyAgain.loading && (buyAgain.data !== undefined || buyAgain.error)

  const suggested = useQuery<ISuggestedItems, IRecommendationVariables>(GET_SUGGESTED_ITEMS, {
    variables,
    skip: !prefillAnswered || prefillItems.length > 0 || !buyAgainAnswered || buyAgainItems.length > 0,
  })
  const suggestedItems = itemsOf(suggested.data?.suggestedItems)

  const [products, source] = useMemo<[RecommendedProduct[], BasketSource | null]>(() => {
    if (prefillItems.length > 0) return [prefillItems.slice(0, BASKET_SIZE), 'prefillCart']
    if (buyAgainItems.length > 0) return [buyAgainItems.slice(0, BASKET_SIZE), 'buyAgain']
    if (suggestedItems.length > 0) return [suggestedItems.slice(0, BASKET_SIZE), 'suggestedItems']
    return [[], null]
    // Keyed on the query results rather than on the filtered arrays: `itemsOf`
    // builds a new array every render, so depending on those would rebuild the
    // basket — and its identity — on every keystroke elsewhere on the page.
  }, [prefill.data, buyAgain.data, suggested.data])

  const quantities = useMemo(() => toSuggestedQuantities(products), [products])

  return {
    products,
    quantities,
    source,
    // Only the query still deciding counts as loading: once one answers with a
    // basket, the section can paint while the skipped ones never run.
    loading: prefill.loading || buyAgain.loading || suggested.loading,
  }
}
